import { CartItem, ItemOption } from "../../types/item";
import {
  previewLineTotal,
  cloneOptions,
  optionAmount,
  checkedCount,
  selectionFilled,
  selectionHint,
  selectionOutOfRange,
  selectionRibbon,
} from "../../utils/menu";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Tools } from "../../utils";
import {
  Checkbox,
  Counter,
  Button,
  Radio,
  Badge,
  Flex,
  Text,
  Card,
  Divider,
} from "../../components";

interface Props extends CartItem {
  onAdd: (data: any, extra: any) => void;
  onError?: (message: string) => void;
}

const Details = ({ onAdd, ...item }: Props) => {
  const [quantity, setCount] = useState(1);
  const [notes, setNotes] = useState(item.notes || "");
  const [options, setOptions] = useState(() => cloneOptions(item.options));
  const [showMissing, setShowMissing] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const groups = useRef<(HTMLDivElement | null)[]>([]);

  const preview = useMemo(
    () => previewLineTotal({ ...item, options, notes }, quantity),
    [item, quantity, options, notes],
  );

  const scrollToGroup = useCallback((index: number) => {
    const pane = scroller.current;
    const group = groups.current[index];
    if (!pane || !group) return;
    const top = Math.max(
      0,
      group.getBoundingClientRect().top -
        pane.getBoundingClientRect().top +
        pane.scrollTop -
        12,
    );
    if (top === pane.scrollTop) return;
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    requestAnimationFrame(() => {
      pane.scrollTo({ top, behavior: reduced ? "auto" : "smooth" });
    });
  }, []);

  const addToCart = () => {
    const index = options.findIndex((option) => selectionOutOfRange(option));
    if (index < 0) {
      setShowMissing(false);
      onAdd({ ...item, options, notes }, { quantity });
      return;
    }
    setShowMissing(true);
    scrollToGroup(index);
  };

  const renderOption = useCallback(
    (option: ItemOption, index: number) => {
      const availableSubs = option.subOptions.map((sub) => ({
        ...sub,
        disabled: !sub.isAvailable,
      }));
      const props = {
        max: option.max,
        vertical: true,
        options: availableSubs.map((sub) => ({
          ...sub,
          label: sub.name,
          value: sub.uuid || sub.name,
          disabled: !sub.isAvailable,
        })),
        value: option.subOptions.reduce(
          (a: any, b: any) => (b.checked ? [...a, b.uuid || b.name] : a),
          [],
        ),
        postAddon: (subOption: any) => {
          const amount = optionAmount(subOption);
          return (
            !!amount && (
              <Flex flex={1} justify="flex-end">
                <Text type="secondary" children={Tools.toCurrency(amount)} />
              </Flex>
            )
          );
        },
        onChange: (e: any) => {
          const clicked = e?.target?.value;
          const idOf = (sub: { uuid?: string; name?: string }) =>
            sub.uuid || sub.name;
          const already =
            option.type !== "checkbox" &&
            !option.required &&
            option.subOptions.some(
              (sub) => sub.checked && idOf(sub) === clicked,
            );
          const selected = !clicked ? e : already ? [] : [clicked];
          const nextCount = option.subOptions.filter(
            (sub) => selected.includes(idOf(sub)) && sub.isAvailable,
          ).length;
          const justFilled =
            !selectionFilled(option, checkedCount(option)) &&
            selectionFilled(option, nextCount);
          if (justFilled && index + 1 < options.length)
            scrollToGroup(index + 1);
          setOptions((current) =>
            current.map((opt, i) => {
              if (i !== index) return opt;
              return {
                ...opt,
                subOptions: opt.subOptions.map((subOption) => ({
                  ...subOption,
                  checked: selected.includes(idOf(subOption)),
                })),
              };
            }),
          );
        },
      };

      const count = checkedCount(option);
      const ribbon = selectionRibbon(option, count);
      const hint = selectionHint(option);
      const unmet = showMissing && selectionOutOfRange(option);
      const cardClass = twMerge(
        "!border-[1.5px] !border-solid transition-[border-color] duration-150",
        unmet && "!border-primary",
      );
      return (
        <div
          ref={(node) => {
            groups.current[index] = node;
          }}
        >
          <Badge.Ribbon type={ribbon.type} text={ribbon.text}>
            <Card classNames={{ body: "p-12" }} className={cardClass}>
              <div className="m-14-b">
                <Text level={4} children={option.name} />
                {!!hint && (
                  <Text
                    type={unmet ? "error" : "secondary"}
                    className="m-2-t"
                    children={hint}
                  />
                )}
                {!!option.description && (
                  <Text className="m-2-t" children={option.description} />
                )}
              </div>
              {option.type === "checkbox" ? (
                <Checkbox.Group {...props} />
              ) : (
                <Radio.Group {...props} value={props.value[0]} />
              )}
            </Card>
          </Badge.Ribbon>
        </div>
      );
    },
    [options, showMissing, scrollToGroup],
  );

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <div ref={scroller} className="min-h-0 flex-1 overflow-auto">
        {!!item.imageUrl && (
          <Flex justify="center">
            <img
              alt={item.name}
              className="w-100 hr-25 object-fit-cover"
              src={Tools.mediaUrl(item.imageUrl)}
            />
          </Flex>
        )}
        <Text children={item.desc} className="m-10-t m-15-h" />
        {!!options?.length && (
          <>
            <Divider />
            <Flex
              vertical
              gap={"1.5rem"}
              className="m-10-t p-15-h"
              children={React.Children.toArray(options.map(renderOption))}
            />
          </>
        )}
        <div className="p-15-h m-20-t m-20-b">
          <Text weight={600} children="Special instructions" />
          <textarea
            className="w-100 m-8-t"
            rows={3}
            value={notes}
            placeholder="Any special requests?"
            onChange={(e) => setNotes(e.target.value)}
            style={{
              padding: "1rem",
              borderRadius: 8,
              border: "1px solid #ddd",
            }}
          />
        </div>
      </div>
      <Flex
        gap={"1rem"}
        className="item-details-footer shrink-0 bg-white p-[2rem] shadow-sticky"
      >
        <Counter
          value={quantity}
          button={{ outlined: true, color: "secondary" }}
          onAdd={() => setCount((value) => value + 1)}
          onRemove={() => quantity > 1 && setCount((value) => value - 1)}
        />
        <Button
          color="base"
          onClick={addToCart}
          className="flex-1"
          title={`Add to Cart (${Tools.toCurrency(preview)})`}
        />
      </Flex>
    </div>
  );
};

export default Details;
